import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/Button";
import { api } from "@/lib/apiClient";
import { addDaysLocal, formatIsoDateShort } from "@/lib/bookingDates";
import type { PitchBlockPeriod, PitchBlockType } from "@/types/camping";

const BLOCK_TYPE_OPTIONS: { value: PitchBlockType; label: string }[] = [
  { value: "full", label: "Vollständig gesperrt" },
  { value: "online_only", label: "Nur Online-Buchung ausgeschlossen" },
];

function formatDateRange(start: string, end: string): string {
  const endLabel = addDaysLocal(end, -1);
  if (start === endLabel) return formatIsoDateShort(start);
  return `${formatIsoDateShort(start)} – ${formatIsoDateShort(endLabel)}`;
}

interface PitchBlockPeriodsEditorProps {
  pitchId: string;
  canEdit: boolean;
}

export function PitchBlockPeriodsEditor({ pitchId, canEdit }: PitchBlockPeriodsEditorProps) {
  const [periods, setPeriods] = useState<PitchBlockPeriod[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    block_type: "full" as PitchBlockType,
    start_date: "",
    end_date: "",
    note: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(() => {
    if (!pitchId) return;
    setLoading(true);
    setError(null);
    api
      .get<PitchBlockPeriod[]>(`/api/v1/pitches/${pitchId}/block-periods`)
      .then(setPeriods)
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, [pitchId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreate = async () => {
    if (!form.start_date || !form.end_date) {
      setError("Bitte Start- und Enddatum angeben.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await api.post(`/api/v1/pitches/${pitchId}/block-periods`, {
        block_type: form.block_type,
        start_date: form.start_date,
        end_date: form.end_date,
        note: form.note.trim() || null,
      });
      setShowForm(false);
      setForm({ block_type: "full", start_date: "", end_date: "", note: "" });
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Speichern fehlgeschlagen.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (periodId: string) => {
    if (!window.confirm("Sperrzeitraum wirklich löschen?")) return;
    setError(null);
    try {
      await api.del(`/api/v1/pitches/${pitchId}/block-periods/${periodId}`);
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Löschen fehlgeschlagen.");
    }
  };

  const blockTypeLabel = (type: PitchBlockType) =>
    BLOCK_TYPE_OPTIONS.find((o) => o.value === type)?.label ?? type;

  return (
    <div className="space-y-3 border-t border-border pt-4">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-label-primary">Sperrzeiträume</h3>
        {canEdit && !showForm ? (
          <Button type="button" variant="secondary" size="sm" onClick={() => setShowForm(true)}>
            Zeitraum hinzufügen
          </Button>
        ) : null}
      </div>
      <p className="text-xs text-label-secondary">
        Legen Sie fest, wann der Platz vollständig gesperrt ist oder nur für die Online-Buchung
        nicht verfügbar sein soll. Abreisedatum ist der erste freie Tag (wie bei Buchungen).
      </p>
      {error ? <p className="text-sm text-status-red">{error}</p> : null}
      {loading ? <p className="text-sm text-label-secondary">Lade Sperrzeiträume…</p> : null}
      {!loading && periods.length === 0 ? (
        <p className="text-sm text-label-secondary">Keine Sperrzeiträume hinterlegt.</p>
      ) : null}
      {periods.length > 0 ? (
        <ul className="space-y-2">
          {periods.map((period) => (
            <li
              key={period.id}
              className="flex flex-wrap items-start justify-between gap-2 rounded-lg border border-border bg-surface-secondary px-3 py-2 text-sm"
            >
              <div>
                <p className="font-medium text-label-primary">
                  {blockTypeLabel(period.block_type)}
                </p>
                <p className="text-label-secondary">
                  {formatDateRange(period.start_date, period.end_date)}
                </p>
                {period.note ? (
                  <p className="text-label-secondary">{period.note}</p>
                ) : null}
              </div>
              {canEdit ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="text-status-red"
                  onClick={() => handleDelete(period.id)}
                >
                  Löschen
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
      {canEdit && showForm ? (
        <div className="space-y-3 rounded-lg border border-border bg-surface-secondary p-3">
          <label className="block text-sm">
            <span className="text-label-secondary">Art der Sperre</span>
            <select
              className="mt-1 w-full rounded-md border border-border bg-surface-primary px-2 py-1.5"
              value={form.block_type}
              onChange={(e) =>
                setForm((f) => ({ ...f, block_type: e.target.value as PitchBlockType }))
              }
            >
              {BLOCK_TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block text-sm">
              <span className="text-label-secondary">Von (inkl.)</span>
              <input
                type="date"
                className="mt-1 w-full rounded-md border border-border bg-surface-primary px-2 py-1.5"
                value={form.start_date}
                onChange={(e) => setForm((f) => ({ ...f, start_date: e.target.value }))}
              />
            </label>
            <label className="block text-sm">
              <span className="text-label-secondary">Bis (exkl., erster freier Tag)</span>
              <input
                type="date"
                className="mt-1 w-full rounded-md border border-border bg-surface-primary px-2 py-1.5"
                value={form.end_date}
                onChange={(e) => setForm((f) => ({ ...f, end_date: e.target.value }))}
              />
            </label>
          </div>
          <label className="block text-sm">
            <span className="text-label-secondary">Notiz (optional)</span>
            <input
              type="text"
              className="mt-1 w-full rounded-md border border-border bg-surface-primary px-2 py-1.5"
              placeholder="z. B. Nässe, Reparatur"
              value={form.note}
              onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
              maxLength={255}
            />
          </label>
          <div className="flex gap-2">
            <Button type="button" size="sm" disabled={submitting} onClick={handleCreate}>
              {submitting ? "Speichern…" : "Speichern"}
            </Button>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => {
                setShowForm(false);
                setError(null);
              }}
            >
              Abbrechen
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
